import { ADMIN_API_PREFIX, fillPath, permissionKeyPathParamSchema, route } from "./routes.js";
import { z } from "zod";
import { PLATFORM_PERMISSION_KEY_PATTERN, appCapabilityKindSchema, requestablePlatformPermissionKeySchema, ringSchema } from "@zion/sdk-contracts";
//#region src/apps-catalog.ts
/**
* `/v1/admin/apps-catalog` — the apps installed on this data plane, their declared permissions,
* their MCP capabilities, their platform-permission consents, and the configuration an org keeps
* after a licence is revoked.
*
* Producers, both mounted at `v1/admin`, both in core-api's `src/admin/`:
* `AdminAppsCatalogController` serves ten of the eleven routes, the grant creation among them, and
* `AdminAppGrantRevocationController` serves the grant revocation alone, in its own file because
* it is the one destructive verb and carries its own authorization argument. Nest composes the
* prefix across classes, so the URL surface is one.
*
* Every response shape here mirrors core-api's `admin-apps-catalog.contract.ts`, whose own
* contract test type-asserts it against the shell's `api/types.ts` in both directions. So these
* three are already in agreement and this package is where that agreement becomes runtime-checkable.
*
* Permissions: `app-permission-grant:read` on every GET, `app-permission-grant:manage` on every
* write. The entitlement gate answers **404, not 403**, for an app the caller's org is not
* entitled to, so an unentitled caller cannot learn which apps exist on the data plane.
*
* ⚠️ One shell-vs-controller disagreement, and it is the sharpest in this workstream. The shell's
* `listAppsCatalog` sends `?simulateRing=<ring>`. **No server reads that parameter anywhere** —
* `listCatalog` binds `@Query('since')` and nothing else — so the request either has no effect or,
* once WS-6 binds a strict query schema, becomes a 400. The query declared below is `since`, the
* controller's own parameter, which the shell never sends.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* One shape travels both ways and therefore stays strict: the `target` union of
* `onboardAppDefaultsRequestSchema`, which the onboarding response echoes back.
*/
/** The collection path. No parameter. */
const ADMIN_APPS_CATALOG_PATH = `${ADMIN_API_PREFIX}/apps-catalog`;
/**
* The retained-configuration path. A literal segment, and core-api declares its handler before
* `:appId` so the literal wins the match; declared after it, a `GET` here would land on the
* detail route and be refused by its uuid pipe.
*/
const ADMIN_APPS_CATALOG_RETAINED_PATH = `${ADMIN_APPS_CATALOG_PATH}/retained`;
/** One app, in OpenAPI `{param}` form. Fill it with {@link adminAppCatalogEntryPath}. */
const ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE = `${ADMIN_APPS_CATALOG_PATH}/{appId}`;
/** One app's version history. Fill it with {@link adminAppCatalogHistoryPath}. */
const ADMIN_APP_CATALOG_HISTORY_PATH_TEMPLATE = `${ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE}/history`;
/** One app's onboarding action. Fill it with {@link adminAppOnboardDefaultsPath}. */
const ADMIN_APP_ONBOARD_DEFAULTS_PATH_TEMPLATE = `${ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE}/onboard-defaults`;
/** One app's capability listing and its toggle. Fill it with {@link adminAppCapabilitiesPath}. */
const ADMIN_APP_CAPABILITIES_PATH_TEMPLATE = `${ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE}/capabilities`;
/**
* One app's grants, the collection that the create route posts to. Fill it with
* {@link adminAppGrantsPath}.
*/
const ADMIN_APP_GRANTS_PATH_TEMPLATE = `${ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE}/grants`;
/**
* One app's grant, addressed by target kind, target id and permission key. Fill it with
* {@link adminAppGrantPath}.
*/
const ADMIN_APP_GRANT_PATH_TEMPLATE = `${ADMIN_APP_GRANTS_PATH_TEMPLATE}/{kind}/{id}/{key}`;
/**
* One app's platform-permission consent. Fill it with
* {@link adminAppPlatformPermissionGrantPath} or
* {@link adminAppPlatformPermissionRevokePath}.
*/
const ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE = `${ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE}/platform-permissions/{key}`;
/**
* `GET /v1/admin/apps-catalog`. Every app installed on this data plane that the caller's org is
* entitled to. Permission `app-permission-grant:read`.
*/
const LIST_APPS_CATALOG_ROUTE = route("GET", ADMIN_APPS_CATALOG_PATH);
/**
* `GET /v1/admin/apps-catalog/retained`. The apps this org is no longer licensed for but still
* holds configuration on. A separate route rather than a widening of the entitled-app predicate,
* which is one definition shared by five surfaces. Permission `app-permission-grant:read`.
*/
const LIST_RETAINED_APPS_ROUTE = route("GET", ADMIN_APPS_CATALOG_RETAINED_PATH);
/**
* `GET /v1/admin/apps-catalog/{appId}`. One app with its declared permissions, its grants and its
* platform-permission requests. Permission `app-permission-grant:read`; 404 for an app this org
* is not entitled to.
*/
const GET_APP_CATALOG_ENTRY_ROUTE = route("GET", ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE);
/**
* `GET /v1/admin/apps-catalog/{appId}/history`. One app's version-change history, newest first.
* Permission `app-permission-grant:read`; 404 for an unentitled app.
*/
const LIST_APP_CATALOG_HISTORY_ROUTE = route("GET", ADMIN_APP_CATALOG_HISTORY_PATH_TEMPLATE);
/**
* `POST /v1/admin/apps-catalog/{appId}/onboard-defaults`. Grants every `isDefault` permission the
* app declares to one target; **201** with what it created and what it skipped. Permission
* `app-permission-grant:manage`; 404 for an unentitled app. A `group` target gets one group
* grant per default key, with the checks of {@link CREATE_APP_GRANT_ROUTE}
* (2026-10-29-app-permission-subjects, D-19). Before the first grant, core-api refuses a group
* target with a 400:
* - `invalid_target` for a `groupId` outside the UUID shape;
* - `unknown_subject` for a group that the org does not hold;
* - a plain message for a default key that the grant route refuses, or for a key over 128
*   characters, which the revoke route cannot carry.
*/
const ONBOARD_APP_DEFAULTS_ROUTE = route("POST", ADMIN_APP_ONBOARD_DEFAULTS_PATH_TEMPLATE);
/**
* `POST /v1/admin/apps-catalog/{appId}/grants`. Grants one app permission key to one target: a
* role, a group, or a user who is an active person of the org (2026-10-29-app-permission-subjects,
* D-4). Body {@link createAppGrantRequestSchema}; **201** with the new grant as one
* {@link appCatalogGrantSchema} row ({@link createAppGrantResponseSchema}). Permission
* `app-permission-grant:manage` and the kernel right `authority.grants` at tier 1, the same pair
* as {@link REVOKE_APP_GRANT_ROUTE}. A `role` target names the role id and keeps every check of
* the role grant path. Refusals:
* - 400 `unauditable_actor`; 400 for a body that fails {@link createAppGrantRequestSchema}, or a
*   key reserved for the org.
* - 400 `invalid_app_or_permission` with a `reason`: `app_not_in_catalog`, one answer for a
*   catalog miss and an app the org does not hold; or `permission_not_declared` with
*   `availableKeys`. A group or user target on an app with no declared key gets
*   `permission_not_declared` with `availableKeys: []` (D-7).
* - 400 `unknown_subject` for a group or user the org does not hold, or a user who is not an
*   active person (D-8); a role the org does not hold answers as the role grant path does.
* - 409 when the grant is active already (`duplicate_grant` for a group or user target).
*/
const CREATE_APP_GRANT_ROUTE = route("POST", ADMIN_APP_GRANTS_PATH_TEMPLATE);
/**
* `DELETE /v1/admin/apps-catalog/{appId}/grants/{kind}/{id}/{key}`. Revokes one app-permission
* grant of a role, a group or a user (D-4 item 4); 204 and no body. Permission
* `app-permission-grant:manage`, the same key as {@link CREATE_APP_GRANT_ROUTE}. 400
* `unsupported_target_kind` for a kind outside {@link appGrantTargetKindSchema}, or
* `invalid_target_id`; 404 `grant_not_found` when no active grant matches, which is deliberately
* the same answer for a grant that does not exist and a target the caller's org cannot see.
*/
const REVOKE_APP_GRANT_ROUTE = route("DELETE", ADMIN_APP_GRANT_PATH_TEMPLATE);
/**
* `GET /v1/admin/apps-catalog/{appId}/capabilities`. The MCP endpoints the app declares and which
* this org has switched on, plus the enablements whose declaration is gone. Permission
* `app-permission-grant:read`; 404 for an unentitled app.
*/
const LIST_APP_CAPABILITIES_ROUTE = route("GET", ADMIN_APP_CAPABILITIES_PATH_TEMPLATE);
/**
* `PUT /v1/admin/apps-catalog/{appId}/capabilities`. Sets one capability's enablement state for
* the caller's org; 200 with the capability and its new state. A body, not a verb, because a
* toggle sets a state. Disabling writes `disabled_at` and never deletes, so absence can never come
* to mean "off". Permission `app-permission-grant:manage`; 400 `capability_not_declared`; 404 for
* an unentitled app or nothing live to disable.
*/
const SET_APP_CAPABILITY_ROUTE = route("PUT", ADMIN_APP_CAPABILITIES_PATH_TEMPLATE);
/**
* `PUT /v1/admin/apps-catalog/{appId}/platform-permissions/{key}`. Records this org's consent to
* one platform permission the app asks for; 200 with the updated request row. No body.
* Permission `app-permission-grant:manage`. `{key}` is bound by the allowlist enum, so a key the
* platform does not offer is a 400 before the handler runs; core-api answers 400 too for a key no
* kept version of the app asks for.
*/
const GRANT_APP_PLATFORM_PERMISSION_ROUTE = route("PUT", ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE);
/**
* `DELETE /v1/admin/apps-catalog/{appId}/platform-permissions/{key}`. Withdraws this org's
* consent; 204 and no body. The withdrawal is live at the app's next read, because the server
* checks the row on every request and no claim carries it. Permission
* `app-permission-grant:manage`.
*
* `{key}` is bound by pattern here and by the allowlist enum on the grant. That asymmetry is the
* design: a consent whose key the platform no longer offers can still be switched off, and can
* never be switched back on.
*/
const REVOKE_APP_PLATFORM_PERMISSION_ROUTE = route("DELETE", ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE);
/**
* The target kinds a grant can name: a role id, a group id, or the user id of a person
* (2026-10-29-app-permission-subjects, D-4). The grant row, the create body and the revoke path
* use the one vocabulary.
*/
const appGrantTargetKindSchema = z.enum([
	"role",
	"group",
	"user"
]);
/**
* A platform permission key, `platform:<segment>:<segment>`. Bounded by
* `PLATFORM_PERMISSION_KEY_PATTERN` from `@zion/sdk-contracts`, the bound the revoke route uses.
*
* ⚠️ Not `permissionKeyPathParamSchema` from `routes.ts`. That one is `PERMISSION_KEY_PATTERN`,
* which is two colon-separated segments and refuses `platform:org-members:read` outright
* (measured). So the two platform-permission builders pass this schema to `fillPath` and the grant
* builder passes the shared one.
*/
const platformPermissionKeySchema = z.string().regex(PLATFORM_PERMISSION_KEY_PATTERN);
/**
* Fill {@link ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE} with one app id.
*
* @param appId the `release.apps_catalog` uuid, which is what `AppCatalogEntry.appId` publishes
*   and what the controller's `ParseUUIDPipe` requires. Not the slug. Throws when it is empty,
*   over 128 characters, or outside the path-param grammar.
*/
function adminAppCatalogEntryPath(appId) {
	return fillPath(ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE, { appId });
}
/**
* Fill {@link ADMIN_APP_CATALOG_HISTORY_PATH_TEMPLATE} with one app id.
*
* @param appId the `release.apps_catalog` uuid. Same bound as {@link adminAppCatalogEntryPath}.
*/
function adminAppCatalogHistoryPath(appId) {
	return fillPath(ADMIN_APP_CATALOG_HISTORY_PATH_TEMPLATE, { appId });
}
/**
* Fill {@link ADMIN_APP_ONBOARD_DEFAULTS_PATH_TEMPLATE} with one app id.
*
* @param appId the `release.apps_catalog` uuid. Same bound as {@link adminAppCatalogEntryPath}.
*/
function adminAppOnboardDefaultsPath(appId) {
	return fillPath(ADMIN_APP_ONBOARD_DEFAULTS_PATH_TEMPLATE, { appId });
}
/**
* Fill {@link ADMIN_APP_CAPABILITIES_PATH_TEMPLATE} with one app id. One path serves the listing
* and the toggle; the method is what separates them.
*
* @param appId the `release.apps_catalog` uuid. Same bound as {@link adminAppCatalogEntryPath}.
*/
function adminAppCapabilitiesPath(appId) {
	return fillPath(ADMIN_APP_CAPABILITIES_PATH_TEMPLATE, { appId });
}
/**
* Fill {@link ADMIN_APP_GRANTS_PATH_TEMPLATE} with one app id: the path of
* {@link CREATE_APP_GRANT_ROUTE}.
*
* @param appId the `release.apps_catalog` uuid. Same bound as {@link adminAppCatalogEntryPath}.
*/
function adminAppGrantsPath(appId) {
	return fillPath(ADMIN_APP_GRANTS_PATH_TEMPLATE, { appId });
}
/**
* Fill {@link ADMIN_APP_GRANT_PATH_TEMPLATE} with one grant's address.
*
* `{key}` carries a colon (`docs:read`), which `pathParamSchema` refuses, so the key rides
* `fillPath`'s third argument with `permissionKeyPathParamSchema` (R-27). `fillPath` encodes every
* segment, so the colon travels as `%3A` and a slash can never split one.
*
* @param appId the `release.apps_catalog` uuid.
* @param kind the grant's target kind: `'role'`, `'group'` or `'user'`.
* @param targetId the target's id: a role id, a group id or the user id of a person, by `kind`.
* @param permissionKey the app permission key, `<segment>:<segment>`. Throws when it fails
*   `PERMISSION_KEY_PATTERN`.
* @returns the request path, every segment percent-encoded, the key included.
*/
function adminAppGrantPath(appId, kind, targetId, permissionKey) {
	return fillPath(ADMIN_APP_GRANT_PATH_TEMPLATE, {
		appId,
		kind: appGrantTargetKindSchema.parse(kind),
		id: targetId,
		key: permissionKey
	}, { key: permissionKeyPathParamSchema });
}
/**
* Fill {@link ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE} for the grant direction.
*
* `{key}` is bound by the allowlist enum through `fillPath`'s schema map, matching the route's own
* bound, so a key the platform does not offer throws here instead of reaching a 400.
*
* @param appId the `release.apps_catalog` uuid.
* @param key a member of `REQUESTABLE_PLATFORM_PERMISSION_KEYS`.
*/
function adminAppPlatformPermissionGrantPath(appId, key) {
	return fillPath(ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE, {
		appId,
		key
	}, { key: requestablePlatformPermissionKeySchema });
}
/**
* Fill {@link ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE} for the revoke direction.
*
* `{key}` is bound by the pattern and not the enum, matching the route's own bound. That is what
* lets a consent whose key the allowlist has since dropped still be switched off.
*
* @param appId the `release.apps_catalog` uuid.
* @param key any key matching `PLATFORM_PERMISSION_KEY_PATTERN`, allowlisted or not.
*/
function adminAppPlatformPermissionRevokePath(appId, key) {
	return fillPath(ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE, {
		appId,
		key
	}, { key: platformPermissionKeySchema });
}
/** One app as the catalog list publishes it. */
const appCatalogEntrySchema = z.object({
	/** `release.apps_catalog.id` — the uuid every path builder here takes. */
	appId: z.string(),
	slug: z.string(),
	name: z.string(),
	icon: z.string().nullable(),
	currentVersion: z.string(),
	ring: ringSchema,
	/**
	* ISO-8601, from `release.apps_catalog.first_seen_at` — when this data plane first saw the app.
	*
	* ⚠️ EXACT for an app first seen after `release/0015` landed, where the column DEFAULT writes it
	* on the operator's first INSERT. For an app that predates that migration this is BEST-EFFORT
	* EARLIEST EVIDENCE and can read LATER than first sight: the one-time backfill takes
	* `MIN(app_version_history.synced_at)` bounded by `synced_at`, so an app catalogued before it
	* entered a ring carries the ring time, and history older than the DP's 90-day retention is
	* already gone. Do not present it to a user as an exact install date for a pre-existing app.
	*
	* ⚠️ #2636 moved the source column and left the field name alone. It was `release.apps_catalog.synced_at`,
	* which the operator re-stamps for every app in the envelope on every full sync, so every app's
	* `firstSeenAt` jumped to the last sync time together and a "new since my cursor" comparison
	* fired for the whole catalog after any envelope change. `first_seen_at` is written once, by the
	* column default on the operator's first insert of the app.
	*/
	firstSeenAt: z.string(),
	/** The org's channels that carry a version of this app. */
	carryingChannels: z.array(z.object({
		id: z.string(),
		name: z.string()
	})),
	declaredPermissionCount: z.number().int().nonnegative(),
	grantCount: z.number().int().nonnegative(),
	/** True when `release.app_degradations` holds a row for this app. */
	degraded: z.boolean()
});
/** One permission an app declares in its manifest. */
const appDeclaredPermissionSchema = z.object({
	/** `<segment>:<segment>`, per `PERMISSION_KEY_PATTERN`. */
	key: z.string(),
	label: z.string(),
	category: z.string(),
	description: z.string().nullable(),
	/** Whether the one-click onboarding action grants this key. */
	isDefault: z.boolean(),
	/**
	* #4416. The app versions on the data plane that declare this key, highest first. A grant on a key
	* that only some versions declare acts only for users whom one of those versions serves. Absent
	* from a core-api that predates the member.
	*/
	versions: z.array(z.string().min(1)).min(1).refine((v) => new Set(v).size === v.length, { message: "versions must not repeat a version" }).readonly().optional()
});
/** One grant of one app permission to one target. */
const appCatalogGrantSchema = z.object({
	targetKind: appGrantTargetKindSchema,
	targetId: z.string(),
	targetName: z.string(),
	permissionKey: z.string(),
	/** ISO-8601. */
	grantedAt: z.string()
});
/**
* The body of {@link CREATE_APP_GRANT_ROUTE} (2026-10-29-app-permission-subjects, D-4 item 1).
* `targetId` is a role id, a group id or the user id of a person, by `targetKind`. `permissionKey`
* takes the bound of {@link permissionKeyPathParamSchema}, 1 to 128 characters of
* `PERMISSION_KEY_PATTERN`, so {@link adminAppGrantPath} can address every grant the route writes.
*/
const createAppGrantRequestSchema = z.strictObject({
	targetKind: appGrantTargetKindSchema,
	targetId: z.uuid(),
	permissionKey: permissionKeyPathParamSchema
});
/** The 201 body of {@link CREATE_APP_GRANT_ROUTE}: the new grant, as the catalog detail lists it. */
const createAppGrantResponseSchema = appCatalogGrantSchema;
/**
* One platform permission an app requests, with this org's consent state.
*
* `label` and `description` are the platform's own copy, carried on the wire, so a renderer
* imports no catalog. `requestedInVersions` is app versions newest first; it is empty for a live
* consent whose request no kept version still makes, and the row still renders and still revokes.
* It holds one version otherwise, the one the data plane runs, because the operator's syncer
* replaces an app's request rows on every reconcile.
*
* `key` is a bare string, wider than the allowlist the grant route binds: a request row or a
* consent row can name a key the allowlist has since dropped.
*/
const appPlatformPermissionSchema = z.object({
	key: z.string(),
	label: z.string(),
	description: z.string(),
	requestedInVersions: z.array(z.string()),
	granted: z.boolean(),
	/** ISO-8601, or null when this org holds no consent. */
	grantedAt: z.string().nullable(),
	/** The user id that consented, or null. */
	grantedBy: z.string().nullable()
});
/** One app's detail view: the list entry plus its permissions, grants and consent rows. */
const appCatalogDetailSchema = appCatalogEntrySchema.extend({
	declaredPermissions: z.array(appDeclaredPermissionSchema),
	grants: z.array(appCatalogGrantSchema),
	/**
	* Every platform permission the app requests and every live consent this org holds for it.
	* Required, not optional: core-api's contract test pins the member both ways.
	*/
	platformPermissionRequests: z.array(appPlatformPermissionSchema)
});
/** What changed between two versions of an app. */
const appChangeSummarySchema = z.object({
	addedPermissions: z.array(z.object({
		key: z.string(),
		label: z.string()
	})),
	removedPermissions: z.array(z.object({
		key: z.string(),
		label: z.string()
	})),
	breakingChange: z.boolean(),
	/** Optional and nullable: both an omitted member and an explicit null are legal on the wire. */
	notes: z.string().nullable().optional()
});
/** One row of an app's version history. */
const appVersionHistoryEntrySchema = z.object({
	version: z.string(),
	ring: ringSchema,
	/** ISO-8601. */
	publishedAt: z.string(),
	changeSummary: appChangeSummarySchema
});
/**
* One capability a fixture declares, with this org's enablement state: an MCP endpoint, or the
* fixture's platform OIDC surface (app-oidc, D8).
*
* Per fixture, not per app: an app may expose several MCP endpoints, so `capabilityId` is unique
* only within a fixture and a row's identity is the pair `(fixtureId, capabilityId)`. A list keyed
* on `capabilityId` alone collapses two endpoints into one.
*/
const appCapabilitySchema = z.object({
	/** `release.fixture_versions.fixture_name`. */
	fixtureId: z.string(),
	/**
	* `mcp` or `oidc`: `appCapabilityKindSchema` of `@zion/sdk-contracts`, the one vocabulary of
	* the capability kind (app-oidc, D8). An `oidc` row carries `capabilityId` `OIDC_CAPABILITY_ID`,
	* one per fixture.
	*/
	capabilityKind: appCapabilityKindSchema,
	capabilityId: z.string(),
	path: z.string(),
	label: z.string(),
	enabled: z.boolean(),
	/** ISO-8601, or null when this org has never switched it on. */
	enabledAt: z.string().nullable()
});
/**
* An enablement this org holds whose declaration is gone — the app no longer declares that
* capability. It still renders and still switches off, which is why the disable direction of
* {@link setAppCapabilityRequestSchema} applies no grammar.
*/
const orphanedCapabilityEnablementSchema = z.object({
	fixtureId: z.string(),
	capabilityKind: appCapabilityKindSchema,
	capabilityId: z.string(),
	/** ISO-8601. Never null: an orphan exists only because it was switched on. */
	enabledAt: z.string()
});
/** `GET .../capabilities` response: the declared capabilities and the orphaned enablements. */
const appCapabilityListingSchema = z.object({
	capabilities: z.array(appCapabilitySchema),
	orphaned: z.array(orphanedCapabilityEnablementSchema)
});
/** One MCP endpoint this org still has switched on, on an app it no longer holds a licence for. */
const retainedCapabilitySchema = z.object({
	/** `release.fixture_versions.fixture_name`. The listing calls the same value `fixtureId`. */
	fixtureName: z.string(),
	capabilityId: z.string()
});
/** One app channel mapping this org still holds, on an app it no longer holds a licence for. */
const retainedChannelBindingSchema = z.object({
	/** `release.customer_channels.name` — the tenant's own display name. */
	customerChannelName: z.string(),
	ringName: z.string()
});
/**
* An app this org is not licensed for but still holds configuration on. The visible half of
* "nothing cascades": a revocation deletes no row, so the configuration survives and has to be
* shown as exactly that.
*
* Three relations survive a revocation and all three are here. Every future relation that
* survives one joins this shape in the change group that creates it — an org holding only a
* channel mapping on an unlicensed app was invisible on every surface until the third member
* landed.
*/
const retainedAppSchema = z.object({
	appId: z.string(),
	slug: z.string(),
	name: z.string(),
	/** The permission keys this org still holds. Items, never a count. */
	permissionSlugs: z.array(z.string()),
	/** The MCP endpoints this org still has switched on. Items, never a count. */
	capabilities: z.array(retainedCapabilitySchema),
	/** The app channel mappings this org still holds. Items, never a count. */
	channelBindings: z.array(retainedChannelBindingSchema)
});
/**
* `GET /v1/admin/apps-catalog` query.
*
* `since` narrows to apps whose `release.apps_catalog.first_seen_at` is later than the value; the
* entitlement predicate is unconditional and `since` narrows on top of it.
*
* ⚠️ #2636 moved the narrowing off `release.apps_catalog.synced_at`, which the operator re-stamps for every
* app on every full envelope sync. A filter on that column answered "changed since" for the whole
* catalog at once, and the only date the client can see is `firstSeenAt`, so it could not tell what
* it had asked. The narrowing now reads the same column the response publishes.
*
* ⛔ An ISO-8601 datetime, not any string `Date.parse` accepts (R-33). The controller checks the
* spelling with `parseWireQueryBound` before it narrows (R-082), so `'2026'`, which `Date.parse`
* would read as midnight on the first of January, is a 400 and never a filter that answers a
* different question from the one asked. `offset: true` so a client may send `Z` or a local
* offset; measured on zod 4.4.3, the bare form refuses `+02:00`. WS-6 binds this schema on the
* route (R-19).
*
* ⚠️ This is the controller's only query parameter. The shell's `simulateRing` has no reader
* anywhere in the tree.
*/
const listAppsCatalogRequestSchema = z.strictObject({ since: z.iso.datetime({ offset: true }).optional().describe("Narrow to apps first seen after this ISO-8601 instant: Z or a +-HH:MM offset through +-23:59, a four-digit year of 0001 or later once rendered in UTC, else 400.") });
/** `GET /v1/admin/apps-catalog` response: every entitled app, unpaged. */
const listAppsCatalogResponseSchema = z.array(appCatalogEntrySchema);
/** `GET /v1/admin/apps-catalog/{appId}` response. */
const getAppCatalogEntryResponseSchema = appCatalogDetailSchema;
/** `GET /v1/admin/apps-catalog/{appId}/history` response: every history row, unpaged. */
const listAppCatalogHistoryResponseSchema = z.array(appVersionHistoryEntrySchema);
/**
* `POST /v1/admin/apps-catalog/{appId}/onboard-defaults` body.
*
* The union is inside one object member rather than three object shapes on the outside, matching
* core-api's `OnboardDefaultsRequestBody`. The two forms are not structurally identical in
* TypeScript even though most callers can use them interchangeably.
*
* The schema accepts any string as `groupId`. core-api grants each default key to that group as
* one group grant, and refuses a `groupId` outside the UUID shape with 400 `invalid_target`
* (D-19 of 2026-10-29-app-permission-subjects). {@link ONBOARD_APP_DEFAULTS_ROUTE} lists every
* refusal of a group target.
*
* ⛔ The `target` union travels in BOTH directions and therefore stays `z.strictObject` on all
* three arms (R-28): {@link onboardAppDefaultsResponseSchema} echoes this member back, so the one
* declaration is read as a request bound and as a response bound. A request member has to refuse
* an unknown key, so strict wins the tie, and a response carrying an extra member inside `target`
* is refused rather than stripped.
*/
const onboardAppDefaultsRequestSchema = z.strictObject({ target: z.discriminatedUnion("kind", [
	z.strictObject({ kind: z.literal("org-members") }),
	z.strictObject({
		kind: z.literal("role"),
		roleSlug: z.string()
	}),
	z.strictObject({
		kind: z.literal("group"),
		groupId: z.string()
	})
]) });
/**
* What one onboard-defaults call DID, as a closed set of named outcomes (#2867).
*
* ## ⛔ WHY A DISCRIMINANT AND NOT A COUNT
*
* `skipped: 0` carries two unrelated facts and a caller cannot act on either. It means "the app
* declares nothing to grant" and it means "this data plane holds no declarations for the app, so
* there was nothing to look at". The second is an operator problem — the app's version has not
* been synced — and the route answers 201 for both. Measured on `dp-app` on 2026-09-17: 17 calls
* across three apps in one hour, every one 201, not one grant written.
*
* ## THE VOCABULARY IS `WriteOutcome`'s, EXTENDED BY TWO
*
* `granted` and `unchanged` are taken verbatim from `WriteOutcome` in the control plane's
* `AppAvailabilityPane`, with the meanings that pane gives them: `unchanged` is a SUCCESS, not an
* error — the write happened and was idempotent. `restored` and `withdrawn` have no counterpart
* here, because this route never revokes. The two zero arms are new, because that pane never had
* a zero case to name.
*
* With `defaultRoles` (zion-cli-integrate-verbs, R-054) `unchanged` also covers a call whose
* named roles are all unknown in the org: the call wrote no grant, and `roleOutcomes` carries one
* `unknown-role` row per pair. The sentence below says so. A fifth value would break the strict
* parse of an older admin app, so the per-role rows carry the detail and the top-level value stays
* one of four.
*
* ⚠️ `no-declarations-synced` is what the data plane OBSERVED, not a diagnosis. The DP cannot see
* why the table is empty: the app may declare no permissions at all, or the operator's reconcile
* may not have delivered this version's declarations. Both are worth the same next step, which is
* to check the app's sync state, so one name covers them and the name says what was seen.
*/
const ONBOARD_APP_DEFAULTS_OUTCOMES = [
	"granted",
	"unchanged",
	"no-defaults-declared",
	"no-declarations-synced"
];
/**
* One sentence per outcome, for a caller to render. The subject is the app's own name, which the
* caller puts in front.
*
* ⛔ A `Record` over the closed union, as one object literal. A fifth outcome is a compile error
* and no arm can be forgotten, and one literal means any test that reads any arm evaluates every
* arm — no branch, and nothing the coverage bar could only reach through a contrived fixture.
* Same construction and same reason as `OUTCOME_PREDICATE` in `AppAvailabilityPane`.
*/
const ONBOARD_APP_DEFAULTS_OUTCOME_SENTENCE = {
	granted: "The default permissions are now granted to the target.",
	unchanged: "Nothing changed. Each default permission already had its grant, or a named role does not exist in this org; the per-role outcomes say which.",
	"no-defaults-declared": "This app declares permissions, and marks none of them a default. There is nothing to grant.",
	"no-declarations-synced": "This data plane holds no permission declarations for this app. Either the app declares none, or its version has not been synced here yet."
};
/**
* Classify one onboard-defaults call from the three counts its producer already holds.
*
* ⛔ THE ORDER OF THE ARMS IS THE RULE. A call that created a key is `granted` whatever else was
* true of the catalog; only then does "were there defaults at all" decide between `unchanged` and
* the two zero arms; and `declaredCount` is the LAST question, because it is the only one that
* separates "the app has a catalog and no defaults in it" from "the app has no catalog here".
*
* @param counts `declaredCount` is every permission this data plane holds a declaration for.
*   `defaultCount` is the subset carrying `isDefault`. `createdCount` is the number of grants the
*   call actually wrote, so a key that was already granted is not in it. Each is a non-negative
*   integer and `createdCount <= defaultCount <= declaredCount` holds for every real call; the
*   function reads them as thresholds and never as a sum, so an out-of-range value cannot make it
*   throw.
* @returns the outcome. It is total over the three counts and has no failure mode.
*/
function classifyOnboardAppDefaults(counts) {
	if (counts.createdCount > 0) return "granted";
	if (counts.defaultCount > 0) return "unchanged";
	if (counts.declaredCount > 0) return "no-defaults-declared";
	return "no-declarations-synced";
}
/** The three things the onboard step can report for one (key, role) pair named by `defaultRoles`. */
const ONBOARD_ROLE_OUTCOMES = [
	"granted",
	"exists",
	"unknown-role"
];
/**
* One `roleOutcomes` item of {@link onboardAppDefaultsResponseSchema}. `key` is the declared
* permission key, `roleSlug` the slug as the manifest wrote it. Strict, as a response member a
* reader switches on.
*/
const onboardRoleOutcomeSchema = z.strictObject({
	key: z.string().min(1),
	roleSlug: z.string().min(1),
	outcome: z.enum(ONBOARD_ROLE_OUTCOMES)
});
/**
* `POST /v1/admin/apps-catalog/{appId}/onboard-defaults` response: the target it granted against,
* the keys it created, how many it skipped as already granted, and which of the four outcomes the
* call had. Without `skipped` the wizard cannot tell an operator whether anything was granted or
* every key was already in place; without `outcome` it cannot tell either of the two zero cases
* apart (#2867).
*/
const onboardAppDefaultsResponseSchema = z.object({
	appId: z.string(),
	/**
	* The request's own union, reused. It stays strict in both directions — see
	* {@link onboardAppDefaultsRequestSchema}.
	*/
	target: onboardAppDefaultsRequestSchema.shape.target,
	createdKeys: z.array(z.string()),
	skipped: z.number().int().nonnegative(),
	/**
	* Optional on the wire, and always emitted by this platform's own producer.
	*
	* ⛔ NEVER `.default()`. A default would fabricate an outcome for a response that carried none,
	* which is the exact defect this member exists to end — `isDefault` was lost because a reader
	* coerced an absent value into a real one.
	*
	* It is optional rather than required because the control plane and a data plane roll
	* independently. A required member breaks a caller the moment it validates a response from a
	* producer that predates the member, and it breaks that producer's build on the way. Absence
	* here is honest and readable: it means the producer predates the discriminant, which is a fact
	* a caller can act on. `classifyOnboardAppDefaults` computes it from counts the producer already
	* holds, so a current producer has no reason to omit it — and `AdminAppsCatalogController`
	* always sets it, pinned by an arm in its own suite rather than by this type.
	*/
	outcome: z.enum(ONBOARD_APP_DEFAULTS_OUTCOMES).optional(),
	/**
	* One item per declared key with `defaultRoles` and per role it names (zion-cli-integrate-verbs
	* R-018, R-027): what the onboard step did for that pair. `granted` is a new grant row. `exists`
	* is a row that was already there, a row the target path of the same call wrote first (a key
	* with `isDefault: true` whose `defaultRoles` names the target role, R-049), or the `owner` role,
	* which holds every key and takes no grant (R-020). `unknown-role` is a slug no role in the org
	* carries.
	*
	* Optional for the same reason as `outcome`: a data plane that predates `defaultRoles` emits
	* no items, and absence means that, never "no roles named". A manifest with no `defaultRoles`
	* on any key yields an empty array from a current producer.
	*/
	roleOutcomes: z.array(onboardRoleOutcomeSchema).optional()
});
/** `GET /v1/admin/apps-catalog/{appId}/capabilities` response. */
const listAppCapabilitiesResponseSchema = appCapabilityListingSchema;
/** The bound on a capability id the enable direction mints. */
const CAPABILITY_ID_MAX_LENGTH = 64;
/** The grammar a minted capability id must match. */
const CAPABILITY_ID_PATTERN = /^[a-z][a-z0-9-]*$/;
/** The bound on a fixture name. Not the manifest's own bound; the door's. */
const CAPABILITY_FIXTURE_ID_MAX_LENGTH = 128;
/**
* `PUT /v1/admin/apps-catalog/{appId}/capabilities` body, discriminated on `enabled`.
*
* The two directions ask different questions of `capabilityId`. Enabling mints a key and must
* only ever mint a well-formed one, so it carries {@link CAPABILITY_ID_PATTERN}. Disabling matches
* a key the table already holds, so it is bounded in length and carries no grammar: an orphaned
* row's declaration is gone by definition, and refusing its key refuses the only control that
* turns it off.
*
* ⚠️ The shell's `SetAppCapabilityRequest` is one flat object with no discrimination, so it does
* not carry the enable arm's grammar. The controller is the fact.
*/
const setAppCapabilityRequestSchema = z.discriminatedUnion("enabled", [z.strictObject({
	fixtureId: z.string().min(1).max(128),
	capabilityKind: appCapabilityKindSchema,
	capabilityId: z.string().min(1).max(64).regex(CAPABILITY_ID_PATTERN),
	enabled: z.literal(true)
}), z.strictObject({
	fixtureId: z.string().min(1).max(128),
	capabilityKind: appCapabilityKindSchema,
	capabilityId: z.string().min(1).max(64),
	enabled: z.literal(false)
})]);
/** `PUT /v1/admin/apps-catalog/{appId}/capabilities` response: the capability and its new state. */
const setAppCapabilityResponseSchema = appCapabilitySchema;
/**
* `PUT /v1/admin/apps-catalog/{appId}/platform-permissions/{key}` response: the updated request
* row. The route takes no body; the key is the whole request.
*/
const grantAppPlatformPermissionResponseSchema = appPlatformPermissionSchema;
/** `GET /v1/admin/apps-catalog/retained` response: every retained app, unpaged. */
const listRetainedAppsResponseSchema = z.array(retainedAppSchema);
/** Every route this file declares, for the package's route census. */
const appsCatalogRoutes = Object.freeze([
	LIST_APPS_CATALOG_ROUTE,
	LIST_RETAINED_APPS_ROUTE,
	GET_APP_CATALOG_ENTRY_ROUTE,
	LIST_APP_CATALOG_HISTORY_ROUTE,
	ONBOARD_APP_DEFAULTS_ROUTE,
	CREATE_APP_GRANT_ROUTE,
	REVOKE_APP_GRANT_ROUTE,
	LIST_APP_CAPABILITIES_ROUTE,
	SET_APP_CAPABILITY_ROUTE,
	GRANT_APP_PLATFORM_PERMISSION_ROUTE,
	REVOKE_APP_PLATFORM_PERMISSION_ROUTE
]);
//#endregion
export { ADMIN_APPS_CATALOG_PATH, ADMIN_APPS_CATALOG_RETAINED_PATH, ADMIN_APP_CAPABILITIES_PATH_TEMPLATE, ADMIN_APP_CATALOG_ENTRY_PATH_TEMPLATE, ADMIN_APP_CATALOG_HISTORY_PATH_TEMPLATE, ADMIN_APP_GRANTS_PATH_TEMPLATE, ADMIN_APP_GRANT_PATH_TEMPLATE, ADMIN_APP_ONBOARD_DEFAULTS_PATH_TEMPLATE, ADMIN_APP_PLATFORM_PERMISSION_PATH_TEMPLATE, CAPABILITY_FIXTURE_ID_MAX_LENGTH, CAPABILITY_ID_MAX_LENGTH, CAPABILITY_ID_PATTERN, CREATE_APP_GRANT_ROUTE, GET_APP_CATALOG_ENTRY_ROUTE, GRANT_APP_PLATFORM_PERMISSION_ROUTE, LIST_APPS_CATALOG_ROUTE, LIST_APP_CAPABILITIES_ROUTE, LIST_APP_CATALOG_HISTORY_ROUTE, LIST_RETAINED_APPS_ROUTE, ONBOARD_APP_DEFAULTS_OUTCOMES, ONBOARD_APP_DEFAULTS_OUTCOME_SENTENCE, ONBOARD_APP_DEFAULTS_ROUTE, ONBOARD_ROLE_OUTCOMES, REVOKE_APP_GRANT_ROUTE, REVOKE_APP_PLATFORM_PERMISSION_ROUTE, SET_APP_CAPABILITY_ROUTE, adminAppCapabilitiesPath, adminAppCatalogEntryPath, adminAppCatalogHistoryPath, adminAppGrantPath, adminAppGrantsPath, adminAppOnboardDefaultsPath, adminAppPlatformPermissionGrantPath, adminAppPlatformPermissionRevokePath, appCapabilityListingSchema, appCapabilitySchema, appCatalogDetailSchema, appCatalogEntrySchema, appCatalogGrantSchema, appChangeSummarySchema, appDeclaredPermissionSchema, appGrantTargetKindSchema, appPlatformPermissionSchema, appVersionHistoryEntrySchema, appsCatalogRoutes, classifyOnboardAppDefaults, createAppGrantRequestSchema, createAppGrantResponseSchema, getAppCatalogEntryResponseSchema, grantAppPlatformPermissionResponseSchema, listAppCapabilitiesResponseSchema, listAppCatalogHistoryResponseSchema, listAppsCatalogRequestSchema, listAppsCatalogResponseSchema, listRetainedAppsResponseSchema, onboardAppDefaultsRequestSchema, onboardAppDefaultsResponseSchema, onboardRoleOutcomeSchema, orphanedCapabilityEnablementSchema, platformPermissionKeySchema, retainedAppSchema, retainedCapabilitySchema, retainedChannelBindingSchema, setAppCapabilityRequestSchema, setAppCapabilityResponseSchema };

//# sourceMappingURL=apps-catalog.js.map